"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Serializable = void 0;
const class_transformer_1 = require("class-transformer");
const class_transformer_validator_1 = require("class-transformer-validator");
const defaultTransformValidationOptions = {
    validator: {
        forbidUnknownValues: true,
    },
};
/**
 * Helper class to help you convert a object(s) or json string object(s) to and from a JavaScript class.
 * @example
 *
  export class Customer extends Serializable {
     ⁣@IsString()
     ⁣@IsRequired()
    public customerId!: string;
  }

  function() {
    const customer = Customer.fromObject({ customerId: '0171383190' }) // valid
    const customers = Customer.fromObject([{ customerId: '0171383190' }]) // valid
    const invalidCustomer = Customer.fromObject({ uuid: '8d76f49a-9cf1-4bd2-9953-3d6a0fd45306' }) // will throw
  }
 */
class Serializable {
    /**
     * Uses class-transformer to convert your class instance to plain JavaScript object.
     * See also https://github.com/typestack/class-transformer/blob/69cd8ccbc672179ea46492a625653ec448f03a17/src/index.ts#L29C1-L29C1
     * @returns
     */
    toJSON() {
        return (0, class_transformer_1.instanceToPlain)(this, {
            exposeUnsetFields: true,
        });
    }
    static fromObject(src) {
        return (0, class_transformer_validator_1.transformAndValidateSync)(this, src, defaultTransformValidationOptions);
    }
    /**
     * Converts a JSON string object to your class instance and validates it against your definition you used with class-validator decorators.
     * Because of TS typings, you are not allowed to pass in a JSON string object array, use {@link fromJSONArray} then.
     * If it is not a valid JSON object it will thrown a SyntaxError.
     * @param this your class instance
     * @param src The JSON string object
     * @returns class instance
     */
    static fromJSON(src) {
        const transformed = (0, class_transformer_validator_1.transformAndValidateSync)(this, src, defaultTransformValidationOptions);
        if (Array.isArray(transformed)) {
            throw new Error('Expected a single instance from JSON string but got an array');
        }
        return transformed;
    }
    /**
     * Converts a plain JavaScript object to your class instance.
     * NOTE: It does not validate. So use on your own risk if you are absolutely sure.
     * @param this your class instance
     * @param src a JavaScript object
     * @returns class instance
     */
    static fromPlain(src) {
        return Object.assign(new this(), src);
    }
    /**
     * Converts a plain JavaScript object to your class instance.
     * NOTE: This method is similar to {@link fromPlain} but it also recursively validates the input object. (Make sure to use `@ValidateNested` decorator for recursive validation).
     * NOTE: This method ignores all decorators that make use of the `Expose` and `Exclude` decorators; this way we can validate with the defined decorators and still use typed input.
     *
     * @example
     * ```ts
  
      class DateRange extends Serializable {
        @IsDateString()
        @IsDefined()
        @ExposeNameCase(ExposeNameCaseEnum.SNAKE_CASE)
        public startDate!: string;
  
        @IsDateString()
        @IsDefined()
        @ExposeNameCase(ExposeNameCaseEnum.SNAKE_CASE)
        public endDate!: string;
      }
  
      class Consumption extends Serializable {
        @Type(() => DateRange)
        @IsDefined()
        @ValidateNested()
        @ExposeNameCase(ExposeNameCaseEnum.SNAKE_CASE)
        public dateRange!: DateRange;
  
        @IsNumber()
        @IsDefined()
        @ExposeNameCase(ExposeNameCaseEnum.SNAKE_CASE)
        public cost!: number;
  
        @IsNumber()
        @IsDefined()
        @ExposeNameCase(ExposeNameCaseEnum.SNAKE_CASE)
        public amountOfEnergyUsed!: number;
      }
  
      function() {
        const caseA = Consumption.fromPlainValidated({}); // Shows type error + will throw a validation error.
        const caseB = Consumption.fromPlainValidated({ dateRange: {startDate: '2020-11-01', endDate: '2021-11-11'}, cost: 1, amountOfEnergyUsed: 1 }); // Valid, note that the `@ExposeNameCase(ExposeNameCaseEnum.SNAKE_CASE)` is ignored!
        const caseC = Consumption.fromPlainValidated({ dateRange: {startDate: '2020-11-0112341234', endDate: '2021-11-11'}, cost: '1', amountOfEnergyUsed: 1} as any as Consumption); // Throws a validation error about startDate not being a valid dateString as well as cost not being a number.
      }
     * ```
     *
     * @param this your class instance
     * @param src the JSON string object
     * @returns a new validated instance of your class
     */
    static fromPlainValidated(src) {
        return (0, class_transformer_validator_1.transformAndValidateSync)(this, src, {
            validator: {
                ...defaultTransformValidationOptions.validator,
            },
            transformer: {
                ignoreDecorators: true,
            },
        });
    }
    /**
     * Converts a JSON string object array to an array of your class instance and validates it against your definition you used with class-validator decorators.
     * Because of TS typings, you are not allowed to pass in a single JSON string object, use {@link fromJSON} then.
     * If it is not a valid JSON object it will thrown a SyntaxError.
     *
     * @param this your class instance
     * @param src The JSON string object
     * @returns array of your class instance
     */
    static fromJSONArray(src) {
        const transformed = (0, class_transformer_validator_1.transformAndValidateSync)(this, src, defaultTransformValidationOptions);
        if (!Array.isArray(transformed)) {
            throw new Error('Expected an array from JSON string instance but got a single instance');
        }
        return transformed;
    }
}
exports.Serializable = Serializable;
